export interface DadosQrNota {
  chave: string
  url?: string
  valorTotal?: number
  dataEmissao?: string
}

/** Dígito verificador da chave de acesso (módulo 11, pesos 2 a 9). */
export function chaveValida(chave: string): boolean {
  const digitos = somenteDigitos(chave)
  if (digitos.length !== 44) return false

  let soma = 0
  let peso = 2
  for (let i = 42; i >= 0; i--) {
    soma += Number(digitos[i]) * peso
    peso = peso === 9 ? 2 : peso + 1
  }

  const resto = soma % 11
  const dv = resto === 0 || resto === 1 ? 0 : 11 - resto
  return dv === Number(digitos[43])
}

export function somenteDigitos(valor: string): string {
  return valor.replace(/\D/g, '')
}

export function formatarChave(chave: string): string {
  return somenteDigitos(chave).replace(/(\d{4})(?=\d)/g, '$1 ').trim()
}

/**
 * Lê o conteúdo do QR Code de uma NFC-e. O parâmetro `p` da URL traz a chave e,
 * na versão 1 do QR Code, também data de emissão e valor.
 */
export function lerConteudoQr(conteudo: string): DadosQrNota | null {
  const texto = conteudo.trim()

  const soDigitos = somenteDigitos(texto)
  if (soDigitos.length === 44 && chaveValida(soDigitos)) return { chave: soDigitos }

  let parametro: string | null = null
  let url: string | undefined

  try {
    const parsed = new URL(texto)
    url = parsed.toString()
    parametro = parsed.searchParams.get('p') ?? parsed.searchParams.get('chNFe')
  } catch {
    const match = texto.match(/[?&]p=([^&]+)/i)
    parametro = match ? decodeURIComponent(match[1]) : null
    if (parametro) url = texto
  }

  if (!parametro) {
    const chaveSolta = texto.match(/\d{44}/)?.[0]
    return chaveSolta && chaveValida(chaveSolta) ? { chave: chaveSolta, url } : null
  }

  const partes = parametro.split('|')
  const chave = somenteDigitos(partes[0] ?? '')
  if (chave.length !== 44 || !chaveValida(chave)) return null

  // Layout da versão 1: chave|versao|ambiente|dhEmi(hex)|vNF|vICMS|digVal|idToken|hash
  const dados: DadosQrNota = { chave, url }
  if (partes.length >= 6) {
    const data = decodificarHex(partes[3])
    if (data && !Number.isNaN(Date.parse(data))) dados.dataEmissao = data
    const valor = Number(partes[4])
    if (Number.isFinite(valor) && valor > 0) dados.valorTotal = valor
  }

  return dados
}

function decodificarHex(valor?: string): string | null {
  if (!valor || !/^[0-9a-f]+$/i.test(valor) || valor.length % 2 !== 0) return null
  let texto = ''
  for (let i = 0; i < valor.length; i += 2) texto += String.fromCharCode(parseInt(valor.slice(i, i + 2), 16))
  return texto
}

export interface ItemCupomOcr {
  descricao: string
  codigo?: string
  quantidade: number
  unidade?: string
  valorUnitario: number
  valorTotal: number
}

export interface DadosCupomOcr {
  valorTotal?: number
  cnpj?: string
  dataEmissao?: string
  chave?: string
  emitente?: string
  itens: ItemCupomOcr[]
}

/** Extrai do texto bruto de um cupom o que dá para conferir: chave, total, CNPJ, data, emitente e itens. */
export function interpretarTextoCupom(texto: string): DadosCupomOcr {
  const linhas = texto.replace(/\r/g, '').split('\n').map((l) => l.trim()).filter(Boolean)
  const dados: DadosCupomOcr = { itens: lerItens(linhas) }

  const chave = extrairChave(linhas)
  if (chave) dados.chave = chave

  // "Valor a pagar" é o que saiu do bolso; "valor total" ainda não tem o desconto.
  dados.valorTotal = valorDaLinha(linhas, /valor\s*a\s*pagar/i)
    ?? valorDaLinha(linhas, /valor\s*total/i)
    ?? valorDaLinha(linhas, /^total\b/i)

  const cnpj = texto.match(/(\d{2})[.\s]?(\d{3})[.\s]?(\d{3})[/\s]?(\d{4})[-\s]?(\d{2})/)
  if (cnpj) dados.cnpj = cnpj.slice(1).join('')

  const autorizacao = texto.match(/autoriza[cç][aã]o[^\d]{0,20}(\d{2})\/(\d{2})\/(\d{4})/i)
  const qualquerData = texto.match(/(\d{2})\/(\d{2})\/(\d{4})/)
  const data = autorizacao ?? qualquerData
  if (data) dados.dataEmissao = `${data[3]}-${data[2]}-${data[1]}`

  const emitente = linhas.find((l) => /[A-Za-zÀ-ÿ]{4,}/.test(l) && !/cnpj|documento|auxiliar/i.test(l))
  if (emitente) dados.emitente = emitente.replace(/\s{2,}/g, ' ')

  return dados
}

/** A chave costuma vir quebrada em grupos e às vezes em duas linhas. */
function extrairChave(linhas: string[]): string | null {
  for (let i = 0; i < linhas.length; i++) {
    const janelas = [linhas[i], `${linhas[i]} ${linhas[i + 1] ?? ''}`]

    for (const janela of janelas) {
      const digitos = janela.replace(/\D/g, '')
      for (let inicio = 0; inicio + 44 <= digitos.length; inicio++) {
        const candidata = digitos.slice(inicio, inicio + 44)
        if (chaveValida(candidata)) return candidata
      }
    }
  }
  return null
}

function valorDaLinha(linhas: string[], rotulo: RegExp): number | undefined {
  for (let i = 0; i < linhas.length; i++) {
    if (!rotulo.test(linhas[i])) continue
    // O valor fica na mesma linha; quando a impressão quebra, cai na seguinte.
    const numero = ultimoNumero(linhas[i]) ?? ultimoNumero(linhas[i + 1] ?? '')
    if (numero !== undefined) return numero
  }
  return undefined
}

function ultimoNumero(linha: string): number | undefined {
  const achados = [...linha.matchAll(/(\d{1,3}(?:[.\s]\d{3})*|\d+)[,.](\d{2})(?!\d)/g)]
  if (achados.length === 0) return undefined

  const ultimo = achados[achados.length - 1]
  const valor = Number(`${ultimo[1].replace(/[.\s]/g, '')}.${ultimo[2]}`)
  return Number.isFinite(valor) && valor > 0 ? valor : undefined
}

/**
 * Item ocupa duas linhas no cupom:
 *   001  144915 SAB LIQ DOVE NUTRICAO 250ML      18,59
 *   1 UN X 18,59 (6,64)
 */
function lerItens(linhas: string[]): ItemCupomOcr[] {
  const itens: ItemCupomOcr[] = []

  for (let i = 0; i < linhas.length; i++) {
    const cabecalho = linhas[i].match(/^(\d{3})\s+(\d{4,8})?\s*(.+?)\s+(\d{1,3}(?:[.\s]\d{3})*|\d+)[,.](\d{2})$/)
    if (!cabecalho) continue

    const total = Number(`${cabecalho[4].replace(/[.\s]/g, '')}.${cabecalho[5]}`)
    const descricao = cabecalho[3].replace(/\s{2,}/g, ' ').trim()
    if (!descricao || !Number.isFinite(total)) continue

    const detalhe = (linhas[i + 1] ?? '').match(/^([\d.,]+)\s*([A-Za-z]{1,3})?\s*[Xx]\s*([\d.,]+)/)
    const quantidade = detalhe ? Number(detalhe[1].replace(',', '.')) || 1 : 1
    const unitario = detalhe ? Number(detalhe[3].replace(/\./g, '').replace(',', '.')) || total : total

    itens.push({
      codigo: cabecalho[2],
      descricao,
      quantidade,
      unidade: detalhe?.[2]?.toUpperCase(),
      valorUnitario: unitario,
      valorTotal: total,
    })

    if (detalhe) i++
  }

  return itens
}
